import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { supabase } from "../../../../src/integrations/supabase/client";
import { formatDistanceToNow } from "date-fns";
import { Users, Activity, BookOpen, Heart, User, Mail } from "lucide-react";

interface ActivityItem {
  id: string;
  type: 'member' | 'activity' | 'article' | 'prayer' | 'message' | 'testimony' | 'magazine';
  title: string;
  description: string;
  timestamp: string;
  author?: string;
}

interface ActivityFeedProps {
  userRole: string;
  userRegion?: string;
}

export const ActivityFeed = ({ userRole, userRegion }: ActivityFeedProps) => {
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchRecentActivities = async () => {
    try {
      setLoading(true);
      const recentActivities: ActivityItem[] = [];

    // Fetch recent members - SKIP for regional admins
    if (userRole !== 'regional_admin') {
      let membersQuery = supabase
        .from('members')
        .select('id, full_name, role, created_at')
        .order('created_at', { ascending: false })
        .limit(5);

      if (userRole === 'regional_admin' && userRegion) {
        membersQuery = membersQuery.eq('region_id', userRegion);
      }

      const { data: members } = await membersQuery;

      if (members) {
        members.forEach(member => {
          recentActivities.push({
            id: member.id,
            type: 'member',
            title: `New member: ${member.full_name}`,
            description: `Added as ${member.role}`,
            timestamp: member.created_at,
          });
        });
      }
    }

      // Fetch recent activities
      let activitiesQuery = supabase
        .from('activities')
        .select('id, title, activity_type, created_at')
        .order('created_at', { ascending: false })
        .limit(5);

      if (userRole === 'regional_admin' && userRegion) {
        activitiesQuery = activitiesQuery.eq('region_id', userRegion);
      }

      const { data: activityData } = await activitiesQuery;

      if (activityData) {
        activityData.forEach(activity => {
          recentActivities.push({
            id: activity.id,
            type: 'activity',
            title: `New activity: ${activity.title}`,
            description: `Type: ${activity.activity_type}`,
            timestamp: activity.created_at,
          });
        });
      }

      // Fetch recent articles
      let articlesQuery = supabase
        .from('articles')
        .select('id, title, author_id, created_at')
        .eq('status', 'published')
        .order('created_at', { ascending: false })
        .limit(5);

      if (userRole === 'regional_admin' && userRegion) {
        articlesQuery = articlesQuery.eq('region_id', userRegion);
      }

      const { data: articles } = await articlesQuery;

      if (articles) {
        articles.forEach(article => {
          recentActivities.push({
            id: article.id,
            type: 'article',
            title: `Article published: ${article.title}`,
            description: 'New article available',
            timestamp: article.created_at,
          });
        });
      }

      // Fetch recent prayer requests
      let prayersQuery = supabase
        .from('prayer_requests')
        .select('id, title, requester_name, created_at')
        .order('created_at', { ascending: false })
        .limit(5);

      if (userRole === 'regional_admin' && userRegion) {
        prayersQuery = prayersQuery.eq('region_id', userRegion);
      }

      const { data: prayers } = await prayersQuery;

      if (prayers) {
        prayers.forEach(prayer => {
          recentActivities.push({
            id: prayer.id,
            type: 'prayer',
            title: `Prayer request: ${prayer.title}`,
            description: `From ${prayer.requester_name || 'Anonymous'}`,
            timestamp: prayer.created_at,
          });
        });
      }

      // Fetch recent contact messages
      let messagesQuery = supabase
        .from('contact_messages')
        .select('id, subject, name, created_at')
        .order('created_at', { ascending: false })
        .limit(5);

      if (userRole === 'regional_admin' && userRegion) {
        messagesQuery = messagesQuery.eq('region_id', userRegion);
      }

      const { data: messages } = await messagesQuery;

      if (messages) {
        messages.forEach(message => {
          recentActivities.push({
            id: message.id,
            type: 'message',
            title: `Contact message: ${message.subject}`,
            description: `From ${message.name}`,
            timestamp: message.created_at,
          });
      });
    }

    // Fetch recent testimonies (for KIT admins, global admins, and presidents)
    if (userRole === 'kit_admin' || userRole === 'global_admin' || userRole === 'president') {
      const { data: testimonies } = await supabase
        .from('testimonies')
        .select('id, title, author_name, created_at, status')
        .order('created_at', { ascending: false })
        .limit(5);

      if (testimonies) {
        testimonies.forEach(testimony => {
          recentActivities.push({
            id: testimony.id,
            type: 'testimony',
            title: `Testimony submitted: ${testimony.title}`,
            description: `By ${testimony.author_name} - Status: ${testimony.status}`,
            timestamp: testimony.created_at,
          });
        });
      }
    }

    // Fetch recent magazines (for KIT admins, global admins, and presidents)
    if (userRole === 'kit_admin' || userRole === 'global_admin' || userRole === 'president') {
      const { data: magazines } = await supabase
        .from('magazines')
        .select('id, title, year, created_at')
        .order('created_at', { ascending: false })
        .limit(5);

      if (magazines) {
        magazines.forEach(magazine => {
          recentActivities.push({
            id: magazine.id,
            type: 'magazine',
            title: `Magazine published: ${magazine.title}`,
            description: `Year ${magazine.year}`,
            timestamp: magazine.created_at,
          });
        });
      }
    }

    // Sort all activities by timestamp and take the 10 most recent
    const sortedActivities = recentActivities
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
      .slice(0, 10);

    setActivities(sortedActivities);
    } catch (error) {
      console.error('Error fetching recent activities:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecentActivities();

    // Set up real-time subscriptions
    let membersChannel;
    if (userRole !== 'regional_admin') {
      membersChannel = supabase
        .channel('members-activity-feed')
        .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'members' }, fetchRecentActivities)
        .subscribe();
    }

    const activitiesChannel = supabase
      .channel('activities-activity-feed')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'activities' }, fetchRecentActivities)
      .subscribe();

    const articlesChannel = supabase
      .channel('articles-activity-feed')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'articles' }, fetchRecentActivities)
      .subscribe();

    const prayersChannel = supabase
      .channel('prayers-activity-feed')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'prayer_requests' }, fetchRecentActivities)
      .subscribe();

    const messagesChannel = supabase
      .channel('messages-activity-feed')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'contact_messages' }, fetchRecentActivities)
      .subscribe();

    const testimoniesChannel = supabase
      .channel('testimonies-activity-feed')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'testimonies' }, fetchRecentActivities)
      .subscribe();

    const magazinesChannel = supabase
      .channel('magazines-activity-feed')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'magazines' }, fetchRecentActivities)
      .subscribe();

    return () => {
      if (membersChannel) supabase.removeChannel(membersChannel);
      supabase.removeChannel(activitiesChannel);
      supabase.removeChannel(articlesChannel);
      supabase.removeChannel(prayersChannel);
      supabase.removeChannel(messagesChannel);
      supabase.removeChannel(testimoniesChannel);
      supabase.removeChannel(magazinesChannel);
    };
  }, [userRole, userRegion]);

  const getIcon = (type: string) => {
    switch (type) {
      case 'member': return Users;
      case 'activity': return Activity;
      case 'article': return BookOpen;
      case 'prayer': return Heart;
      case 'message': return Mail;
      case 'testimony': return Heart;
      case 'magazine': return BookOpen;
      default: return User;
    }
  };

  const getBadgeVariant = (type: string) => {
    switch (type) {
      case 'member': return 'default';
      case 'activity': return 'secondary';
      case 'article': return 'outline';
      case 'prayer': return 'destructive';
      case 'message': return 'default';
      case 'testimony': return 'secondary';
      case 'magazine': return 'outline';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Recent Activity</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-center space-x-3 animate-pulse">
                <div className="w-8 h-8 bg-muted rounded-full"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-muted rounded w-3/4"></div>
                  <div className="h-3 bg-muted rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Activity className="h-5 w-5" />
          Recent Activity
        </CardTitle>
      </CardHeader>
      <CardContent>
        {activities.length === 0 ? (
          <div className="text-center py-6 text-muted-foreground">
            <Activity className="h-12 w-12 mx-auto mb-2 opacity-50" />
            <p>No recent activity</p>
          </div>
        ) : (
          <div className="space-y-4">
            {activities.map((activity) => {
              const IconComponent = getIcon(activity.type);
              return (
                <div key={activity.id} className="flex items-start space-x-3 p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors">
                  <div className="bg-background p-2 rounded-full border">
                    <IconComponent className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-sm font-medium truncate">{activity.title}</p>
                      <Badge variant={getBadgeVariant(activity.type)} className="text-xs">
                        {activity.type}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">{activity.description}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {formatDistanceToNow(new Date(activity.timestamp), { addSuffix: true })}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};